// Session PJ1F (PJ2 Stage 9, PJ1 Stage F): "Create purchase order" on a project's survey view. It opens already filled in from the
// REVIEWED lift surveys (one line for each, and the notes carry that lift's worked out figures); the office types the supplier and
// its quotation reference, puts the parts and costs from that quotation on the lines, and saves. Saving dispatches the ordinary
// POST /api/purchase-orders with the project's id, so the order is a draft that goes nowhere until the office says it was placed. The
// order for the supplier (a PDF, and a Gmail DRAFT, never a send) is offered straight after, on the same panel the Purchase orders
// page uses. No new page or menu item: this is a window on the screen the office is already on.
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { api, type ProjectSurveyPurchaseOrderPrefill, type PurchaseOrderItemInput, type PurchaseOrderListItem } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import { MODAL_ACTIONS, MODAL_SECONDARY_BTN, ModalShell } from "../../../components/ModalShell";
import { SupplierOrderDraftPanel } from "../purchaseOrders/SupplierOrderDraftPanel";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function PurchaseOrderFromSurveyModal({ projectId, onClose, onCreated }: { projectId: string; onClose: () => void; onCreated?: () => void }) {
  const [prefill, setPrefill] = useState<ProjectSurveyPurchaseOrderPrefill | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [supplier, setSupplier] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [quoteRef, setQuoteRef] = useState("");
  const [items, setItems] = useState<PurchaseOrderItemInput[]>([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<PurchaseOrderListItem | null>(null);

  useEffect(() => {
    let alive = true;
    api.projectSurveysOffice
      .purchaseOrderPrefill(projectId)
      .then((p) => {
        if (!alive) return;
        setPrefill(p);
        setItems(p.items.length > 0 ? p.items.map((it) => ({ description: it.description, partCode: "", quantity: it.quantity, unitCost: null })) : [{ description: "", partCode: "", quantity: 1, unitCost: null }]);
        setNotes(p.notes);
      })
      .catch((e) => {
        if (alive) setLoadError(friendlyApiError(e, "Could not load the surveys for the order."));
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const setItem = (i: number, patch: Partial<PurchaseOrderItemInput>) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!supplier.trim()) return setError("Enter the supplier's name.");
    const cleaned = items.filter((it) => it.description.trim());
    if (cleaned.length === 0) return setError("Add at least one item.");
    for (const it of cleaned) if (!it.quantity || it.quantity < 1) return setError(`Give a quantity for "${it.description}".`);
    if (supplierEmail.trim() && !/^\S+@\S+\.\S+$/.test(supplierEmail.trim())) return setError("That is not a valid email address.");
    setBusy(true);
    setError(null);
    try {
      const order = await api.purchaseOrders.create({
        supplier: supplier.trim(),
        projectId,
        supplierQuoteRef: quoteRef.trim() || null,
        supplierEmail: supplierEmail.trim() || null,
        notes: notes.trim() || null,
        items: cleaned.map((it) => ({ partCode: it.partCode?.trim() || null, description: it.description.trim(), quantity: it.quantity, unitCost: it.unitCost ?? null })),
      });
      setCreated(order);
      onCreated?.();
    } catch (e2) {
      setError(friendlyApiError(e2, "Could not save the purchase order."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Create purchase order" onClose={() => !busy && onClose()} width="max-w-3xl" panelClassName="p-5 max-h-[90vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">Create purchase order</h2>
      {loadError ? <p role="alert" className="mt-3 text-sm text-ele-danger">{loadError}</p> : null}
      {!prefill && !loadError ? <Loader shape="inline" label="Loading the surveys..." className="mt-3" /> : null}

      {created ? (
        <>
          <p role="status" className="mt-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong" data-order-created>
            Saved as {created.reference}, a draft. It has not been sent to anyone. You can see it on the <Link to="/office/purchase-orders" className="text-ele-green underline-offset-2 hover:underline">Purchase orders</Link> page, where you mark it ordered once it has been placed and record what arrives.
          </p>
          <div className="mt-4">
            <SupplierOrderDraftPanel order={created} />
          </div>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose}>Close</button>
          </div>
        </>
      ) : prefill ? (
        <form onSubmit={save} className="mt-2 grid gap-3">
          <p className="text-sm text-ele-body">
            For {prefill.project.projectRef}
            {prefill.project.siteName ? `, ${prefill.project.siteName}` : ""}. Filled in from the reviewed lift surveys: change anything before you save. The order goes to the supplier as a PDF that names the site and never the customer, so keep the customer's name out of the items and notes.
          </p>
          {prefill.leftOut.length > 0 ? (
            <p className="text-sm text-[var(--ele-amber-text)]">
              Not reviewed yet, so left out: {prefill.leftOut.join(", ")}. Review a lift's survey first to start its line.
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-3">
            <label>
              <span className={labelCls}>Supplier</span>
              <input aria-label="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} className={inputCls} />
            </label>
            <label>
              <span className={labelCls}>Supplier's email (optional)</span>
              <input aria-label="Supplier's email" type="email" value={supplierEmail} onChange={(e) => setSupplierEmail(e.target.value)} className={inputCls} />
            </label>
            <label>
              <span className={labelCls}>Their quotation reference</span>
              <input aria-label="Supplier quotation reference" value={quoteRef} onChange={(e) => setQuoteRef(e.target.value)} className={inputCls} />
            </label>
          </div>

          <div>
            <span className={labelCls}>Items</span>
            <div className="grid gap-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_5rem_5rem_2rem] items-center gap-2 max-sm:grid-cols-[1fr_4rem_4.5rem_2rem]">
                  <input aria-label={`Item ${i + 1} description`} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="Description" className={inputCls} />
                  <input aria-label={`Item ${i + 1} quantity`} type="number" min={1} value={it.quantity} onChange={(e) => setItem(i, { quantity: Number(e.target.value) })} className={inputCls} />
                  <input
                    aria-label={`Item ${i + 1} unit cost`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={it.unitCost ?? ""}
                    onChange={(e) => setItem(i, { unitCost: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="Cost £"
                    className={inputCls}
                  />
                  <button type="button" aria-label={`Remove item ${i + 1}`} onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))} className="text-ele-body hover:text-ele-danger max-sm:min-h-11">
                    <Trash2 aria-hidden size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setItems((prev) => [...prev, { description: "", partCode: "", quantity: 1, unitCost: null }])} className="mt-2 font-menu text-xs tracking-wide text-ele-green hover:underline max-sm:min-h-11">
              + Add another item
            </button>
          </div>

          <label>
            <span className={labelCls}>Notes for the supplier</span>
            <textarea aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} className={inputCls} />
          </label>

          {error ? <p role="alert" className="rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p> : null}
          <div className={`mt-2 gap-3 ${MODAL_ACTIONS}`}>
            <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>Cancel</button>
            <Button type="submit" size="sm" disabled={busy} className="max-sm:w-full">
              {busy ? "Saving..." : "Save as draft"}
            </Button>
          </div>
        </form>
      ) : null}
    </ModalShell>
  );
}
